import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Panel, CheckPicker, Input, InputGroup, Button } from 'rsuite';
import { isMobile } from 'react-device-detect';
import { Link, useSearchParams } from 'react-router-dom';

import NavigationBar from '../Shared/NavigationBar';
import RulesDisclaimer from '../Shared/RulesDisclaimer';
import useScrollToTop from '../../helpers/useScrollToTop';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';
import { KEYWORD_GLOSSARY, KEYWORD_CATEGORIES, KEYWORD_THEMES } from '../../helpers/rulesData';
import useDebouncedSearch from '../../helpers/useDebouncedSearch';

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 24,
  containerPaddingMobile: 12,
  headerMarginTop: 24,
  headerMarginBottom: 24,
  titleMarginBottom: 8,
  filtersMarginBottom: 20,
  filtersGap: 12,
  cardMarginBottom: 16,
  cardPadding: 20,
  cardBorderRadius: 6,
  resultCountMarginBottom: 16,
  letterBarMarginBottom: 20,
  letterBarPadding: '4px 8px',
  letterBarBorderRadius: 4,
  clearButtonMarginLeft: 8,
  rulesRefPadding: '2px 8px',
  rulesRefBorderRadius: 4,
  emptyStatePadding: 40,
  backLinkMarginBottom: 16,
  backLinkPadding: '10px 0',
  categoryBadgePadding: '2px 8px',
  categoryBadgeBorderRadius: 10,
  themeBadgePadding: '2px 8px',
  themeBadgeBorderRadius: 10,
  letterBarGap: 4,
  checkPickerMinWidth: 200,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  subtitle: 15,
  subtitleMobile: 14,
  keywordName: 20,
  keywordNameWeight: 600,
  reminder: 14,
  explanation: 14,
  explanationLineHeight: 1.6,
  rulesRef: 14,
  resultCount: 14,
  letterBarItem: 14,
  letterBarItemWeight: 600,
  backLink: 14,
  emptyState: 15,
  categoryBadge: 14,
  categoryBadgeWeight: 600,
  categoryBadgeLetterSpacing: 1,
  themeBadge: 14,
  themeBadgeWeight: 600,
  themeBadgeLetterSpacing: 1,
};

const DEBOUNCE_MS = 300;

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const CATEGORY_PICKER_DATA = Object.values(KEYWORD_CATEGORIES).map((cat) => ({
  label: cat,
  value: cat,
}));

const THEME_PICKER_DATA = Object.values(KEYWORD_THEMES).map((theme) => ({
  label: theme,
  value: theme,
}));

const KeywordGlossary = () => {
  const { preferences } = usePreferences();
  const t = getThemeColors(preferences.theme);
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([]);
  const letterRefs = useRef({});

  useScrollToTop();

  const handleDebouncedSearch = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const [inputValue, setInputValue] = useDebouncedSearch(
    initialQuery,
    handleDebouncedSearch,
    DEBOUNCE_MS
  );

  const filteredKeywords = useMemo(() => {
    let result = KEYWORD_GLOSSARY;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (kw) => kw.name.toLowerCase().includes(term)
      );
    }

    if (selectedCategories.length > 0) {
      result = result.filter((kw) => selectedCategories.includes(kw.category));
    }

    if (selectedThemes.length > 0) {
      result = result.filter((kw) => selectedThemes.includes(kw.theme));
    }

    return [...result].sort((a, b) => a.name.localeCompare(b.name));
  }, [searchTerm, selectedCategories, selectedThemes]);

  const availableLetters = useMemo(() => {
    const letters = new Set(filteredKeywords.map((kw) => kw.name[0].toUpperCase()));
    return letters;
  }, [filteredKeywords]);

  const scrollToLetter = useCallback((letter) => {
    const el = letterRefs.current[letter];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleClearFilters = useCallback(() => {
    setInputValue('');
    setSearchTerm('');
    setSelectedCategories([]);
    setSelectedThemes([]);
  }, [setInputValue]);

  const hasFilters = searchTerm || selectedCategories.length > 0 || selectedThemes.length > 0;

  let currentLetter = '';

  return (
    <>
      <NavigationBar />
      <div
        style={{
          maxWidth: SPACING.containerMaxWidth,
          margin: '0 auto',
          padding: isMobile
            ? SPACING.containerPaddingMobile
            : SPACING.containerPadding,
        }}
      >
        {/* Back link */}
        <div style={{ marginTop: SPACING.headerMarginTop, marginBottom: SPACING.backLinkMarginBottom }}>
          <Link
            to="/rules"
            style={{ color: t.accentPrimary, fontSize: FONT.backLink, textDecoration: 'none', display: 'inline-block', padding: SPACING.backLinkPadding }}
          >
            ← Back to Rules Reference
          </Link>
        </div>

        {/* Header */}
        <div style={{ marginBottom: SPACING.headerMarginBottom }}>
          <h1
            style={{
              fontSize: isMobile ? FONT.titleMobile : FONT.title,
              color: t.textPrimary,
              marginBottom: SPACING.titleMarginBottom,
            }}
          >
            🔑 Keyword Glossary
          </h1>
          <p
            style={{
              fontSize: isMobile ? FONT.subtitleMobile : FONT.subtitle,
              color: t.textMuted,
              margin: 0,
            }}
          >
            A searchable reference of all evergreen and common keyword abilities
          </p>
        </div>

        {/* Filters */}
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: SPACING.filtersGap,
            marginBottom: SPACING.filtersMarginBottom,
            alignItems: isMobile ? 'stretch' : 'center',
          }}
        >
          <InputGroup inside style={{ flex: 1 }}>
            <Input
              placeholder="Search keywords by name..."
              value={inputValue}
              onChange={setInputValue}
            />
            <InputGroup.Addon>🔍</InputGroup.Addon>
          </InputGroup>
          <CheckPicker
            data={CATEGORY_PICKER_DATA}
            value={selectedCategories}
            onChange={setSelectedCategories}
            placeholder="Filter by category"
            searchable={false}
            style={{ minWidth: isMobile ? undefined : SPACING.checkPickerMinWidth }}
          />
          <CheckPicker
            data={THEME_PICKER_DATA}
            value={selectedThemes}
            onChange={setSelectedThemes}
            placeholder="Filter by theme"
            searchable={false}
            style={{ minWidth: isMobile ? undefined : SPACING.checkPickerMinWidth }}
          />
          {hasFilters && (
            <Button
              appearance="link"
              onClick={handleClearFilters}
              style={{ color: t.accentPrimary, marginLeft: isMobile ? 0 : SPACING.clearButtonMarginLeft }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Result count */}
        <div style={{ marginBottom: SPACING.resultCountMarginBottom }}>
          <span style={{ fontSize: FONT.resultCount, color: t.textFaint }}>
            Showing {filteredKeywords.length} of {KEYWORD_GLOSSARY.length} keywords
          </span>
        </div>

        {/* Alphabetical index bar */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: SPACING.letterBarGap,
            marginBottom: SPACING.letterBarMarginBottom,
          }}
        >
          {ALPHABET.map((letter) => {
            const isActive = availableLetters.has(letter);
            return (
              <span
                key={letter}
                onClick={() => isActive && scrollToLetter(letter)}
                style={{
                  fontSize: FONT.letterBarItem,
                  fontWeight: FONT.letterBarItemWeight,
                  color: isActive ? t.accentPrimary : t.textFaint,
                  cursor: isActive ? 'pointer' : 'default',
                  padding: SPACING.letterBarPadding,
                  borderRadius: SPACING.letterBarBorderRadius,
                }}
                onMouseEnter={(e) => {
                  if (isActive) e.currentTarget.style.color = t.accentPrimary;
                }}
                onMouseLeave={(e) => {
                  if (isActive) e.currentTarget.style.color = t.accentPrimary;
                }}
              >
                {letter}
              </span>
            );
          })}
        </div>

        {/* Keyword cards */}
        {filteredKeywords.length > 0 ? (
          filteredKeywords.map((kw) => {
            const firstLetter = kw.name[0].toUpperCase();
            let showLetterHeader = false;
            if (firstLetter !== currentLetter) {
              currentLetter = firstLetter;
              showLetterHeader = true;
            }

            return (
              <div key={kw.name}>
                {showLetterHeader && (
                  <div
                    ref={(el) => {
                      letterRefs.current[firstLetter] = el;
                    }}
                    style={{
                      fontSize: FONT.keywordName,
                      fontWeight: FONT.keywordNameWeight,
                      color: t.accentPrimary,
                      marginTop: SPACING.cardMarginBottom,
                      marginBottom: SPACING.filtersGap,
                    }}
                  >
                    {firstLetter}
                  </div>
                )}
                <Panel
                  bordered
                  style={{
                    backgroundColor: t.cardBackground,
                    borderColor: t.borderPrimary,
                    marginBottom: SPACING.cardMarginBottom,
                    borderRadius: SPACING.cardBorderRadius,
                  }}
                >
                  <div style={{ padding: SPACING.cardPadding }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.filtersGap, marginBottom: SPACING.filtersGap }}>
                      <h3
                        style={{
                          fontSize: FONT.keywordName,
                          fontWeight: FONT.keywordNameWeight,
                          color: t.textPrimary,
                          margin: 0,
                        }}
                      >
                        {kw.name}
                      </h3>
                      <span
                        style={{
                          fontSize: FONT.categoryBadge,
                          fontWeight: FONT.categoryBadgeWeight,
                          color: t.textMuted,
                          backgroundColor: t.borderPrimary,
                          padding: SPACING.categoryBadgePadding,
                          borderRadius: SPACING.categoryBadgeBorderRadius,
                          textTransform: 'uppercase',
                          letterSpacing: FONT.categoryBadgeLetterSpacing,
                        }}
                      >
                        {kw.category}
                      </span>
                      {kw.theme && (
                        <span
                          style={{
                            fontSize: FONT.themeBadge,
                            fontWeight: FONT.themeBadgeWeight,
                            color: '#6ecf9a',
                            backgroundColor: '#1a3a2a',
                            padding: SPACING.themeBadgePadding,
                            borderRadius: SPACING.themeBadgeBorderRadius,
                            textTransform: 'uppercase',
                            letterSpacing: FONT.themeBadgeLetterSpacing,
                          }}
                        >
                          {kw.theme}
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        fontSize: FONT.reminder,
                        color: t.accentPrimary,
                        fontStyle: 'italic',
                        marginBottom: SPACING.filtersGap,
                      }}
                    >
                      {kw.reminder}
                    </p>
                    <p
                      style={{
                        fontSize: FONT.explanation,
                        color: t.textSecondary,
                        lineHeight: FONT.explanationLineHeight,
                        marginBottom: SPACING.filtersGap,
                      }}
                    >
                      {kw.explanation}
                    </p>
                    <span
                      style={{
                        fontSize: FONT.rulesRef,
                        color: t.accentPrimary,
                        backgroundColor: t.borderPrimary,
                        padding: SPACING.rulesRefPadding,
                        borderRadius: SPACING.rulesRefBorderRadius,
                      }}
                    >
                      {kw.rulesReference === 'N/A' ? 'N/A' : `CR ${kw.rulesReference}`}
                    </span>
                  </div>
                </Panel>
              </div>
            );
          })
        ) : (
          <div style={{ textAlign: 'center', padding: SPACING.emptyStatePadding }}>
            <p style={{ color: t.textFaint, fontSize: FONT.emptyState }}>
              No keywords match your search.
            </p>
          </div>
        )}
        <RulesDisclaimer />
      </div>
    </>
  );
};

export default KeywordGlossary;
